/**
 * Die Buchung -- die zentrale Entitaet des Systems (KONZEPT.md §9.2).
 *
 * `booking_event` ist ein Append-only-Log: kein UPDATE, kein DELETE, nie
 * (CLAUDE.md §3.2). Der Zustand eines Artikels wird daraus abgeleitet und
 * nirgends unabhaengig gepflegt.
 *
 * Felder, die `null` sein duerfen, sind bewusst `nullable` und nicht
 * `optional`: Eine Buchung, die durch die Offline-Warteschlange, ueber JSON
 * und durch die Datenbank gewandert ist, soll danach exakt dieselbe Gestalt
 * haben wie vorher. `undefined` ueberlebt diesen Weg nicht.
 */
import { z } from "zod";
import { GeraeteKennung, Id, LangText, Zeitpunkt } from "./basis.js";
import { BuchungsArt, Status } from "./enums.js";
/** Menge einer Bewegung. Ganzzahlig und positiv -- die Richtung sagt die Art. */
export const Menge = z.number().int().nonnegative();
/**
 * Eine Buchung, so wie sie im Log steht.
 *
 * Die Pflichtfelder je Buchungsart pruefen nicht die Feldtypen, sondern
 * `BuchungAnlegen` weiter unten. Beim *Lesen* aus dem Log wird bewusst nicht
 * mehr geprueft: Was einmal drin steht, muss lesbar bleiben, auch wenn sich
 * die Regeln spaeter aendern.
 */
export const Buchung = z.object({
    /**
     * Wird **auf dem Client** erzeugt (UUID v7, CLAUDE.md §3.2).
     *
     * Zwei Gruende: Der Server erkennt daran doppelt uebertragene Buchungen und
     * verarbeitet sie nur einmal. Und eine offline erfasste Buchung behaelt ihre
     * Identitaet von der Erfassung bis in alle Auswertungen -- es gibt keinen
     * Moment, in dem sie eine neue Nummer bekommt.
     */
    id: Id,
    /**
     * Fortlaufende Nummer in der Reihenfolge, in der der Server die Buchungen
     * festgeschrieben hat. Vergibt der Server.
     *
     * Sie ist der Faden, an dem sich ein Client beim Abgleich entlanghangelt:
     * "gib mir alles ab Nummer N". Zeitstempel taugen dafuer nicht -- zwei
     * Buchungen koennen denselben tragen, und Geraeteuhren gehen falsch.
     */
    folge: z.number().int().positive(),
    artikel_id: Id,
    art: BuchungsArt,
    /**
     * Ziel der Bewegung, wenn sie auf einen realen Lagerort geht.
     *
     * Bei `einsatz`, `entnehmen` und `ausgabe` steht hier seit Vertrag 2.4 das
     * Packstueck, in dem der Artikel mitgeht -- bei abgeleiteten Buchungen
     * (KONZEPT.md §5.2, ADR 0012). Der Artikel bleibt dann dem Packstueck
     * zugeordnet, auch waehrend es draussen ist.
     *
     * Seit Vertrag 2.5 auch bei direkten Buchungen: Wer einen Artikel in ein
     * Packstueck verschiebt, das gerade im Einsatz oder verliehen ist, bucht
     * `einsatz` bzw. `ausgabe` mit dem Packstueck als Ziel (ADR 0014). Ebenso
     * „Rueckgaengig“ zurueck in einen solchen Zustand.
     */
    ziel_lagerort_id: Id.nullable(),
    /** Einsatzstelle bei `art: "einsatz"`. Darf leer bleiben (KONZEPT.md §6.1). */
    einsatzstelle_id: Id.nullable(),
    /** Bewegungsmenge bei Verbrauchsmaterial. Bei Stueckgut immer `null`. */
    menge: Menge.nullable(),
    /**
     * Bestand **nach** dieser Buchung, nur bei `art: "korrektur"`.
     *
     * Eine Inventur zaehlt einen absoluten Wert. Haette sie stattdessen eine
     * Differenz zu buchen, muesste das Geraet den aktuellen Bestand kennen --
     * offline ist der aber moeglicherweise veraltet, und die Differenz waere
     * falsch. Ein absoluter Wert ist unabhaengig davon richtig.
     */
    menge_absolut: Menge.nullable(),
    /**
     * Zielstatus bei `art: "statusaenderung"` und `art: "korrektur"` -- der
     * Schluessel eines Status aus den Stammdaten.
     *
     * Beim Lesen nur als Zeichenkette geprueft: Buchungen aus Vertrag 1.x koennen
     * hier noch einen Verbleib wie `im_lager` tragen, und was im Log steht, muss
     * lesbar bleiben. Beim Anlegen gilt die strenge Regel (`BuchungAnlegen`).
     */
    status: z.string().max(40).nullable(),
    benutzer_id: Id,
    /**
     * Zeitpunkt der Erfassung **auf dem Geraet**. Massgeblich fuer die
     * Konfliktaufloesung (KONZEPT.md §15.3).
     */
    erfasst_am: Zeitpunkt,
    /** Zeitpunkt des Eingangs auf dem Server. Wird nie vom Client gesetzt. */
    empfangen_am: Zeitpunkt,
    /** Erfassendes Geraet. Entscheidet Gleichstaende bei `erfasst_am`. */
    geraet: GeraeteKennung,
    /** Klammert mehrere Buchungen zu einem Vorgang, etwa einer Ausgabe. */
    vorgang_id: Id.nullable(),
    /**
     * Bei abgeleiteten Buchungen: die ausloesende Buchung (KONZEPT.md §5.2).
     *
     * Wird ein Packstueck gebucht, erzeugt der Core fuer jeden enthaltenen
     * Artikel eine eigene Buchung mit diesem Verweis. So stimmt die Historie
     * jedes Einzelteils, ohne dass jemand einzeln scannen muss.
     */
    abgeleitet_von: Id.nullable(),
    /** Welches Modul die Buchung ausgeloest hat; `null` = der Core selbst. */
    modul: z.string().max(64).nullable(),
    kommentar: LangText.nullable(),
    fotos: z.array(Id),
});
/**
 * Was ein Client schickt, um eine Buchung anzulegen.
 *
 * `empfangen_am` fehlt bewusst -- den setzt der Server. Alles andere kommt
 * vom Geraet, auch die `id` und `erfasst_am`.
 */
/**
 * Die Felder einer neuen Buchung -- ohne die Fachregeln.
 *
 * Das ist die Gestalt, in der Buchungen **uebertragen** werden. Sie prueft nur,
 * ob die Felder stimmen, nicht ob die Buchung fachlich taugt.
 *
 * Der Grund ist der Offline-Abgleich: Nach einem Einsatz kommen dreissig
 * Buchungen in einem Stapel an. Wuerde die Anfrage als Ganzes an der ersten
 * fehlerhaften scheitern, kaeme keine einzige an -- und das Geraet haette
 * keine Moeglichkeit zu erkennen, welche. Die fachliche Pruefung geschieht
 * deshalb **je Position** im Core-Dienst, und jede Position bekommt ihre
 * eigene Rueckmeldung (KONZEPT.md §2, L4 -- nie blockieren).
 */
export const BuchungFelder = Buchung.omit({ empfangen_am: true, folge: true }).extend({
    ziel_lagerort_id: Id.nullable().default(null),
    einsatzstelle_id: Id.nullable().default(null),
    menge: Menge.nullable().default(null),
    menge_absolut: Menge.nullable().default(null),
    // Nur die Gestalt; ob es ein gueltiger Status ist, entscheidet sich je
    // Position. Ein Geraet mit alter Warteschlange soll nicht den ganzen Stapel
    // verlieren, weil eine Buchung einen alten Wert traegt.
    status: z.string().max(40).nullable().default(null),
    vorgang_id: Id.nullable().default(null),
    abgeleitet_von: Id.nullable().default(null),
    modul: z.string().max(64).nullable().default(null),
    kommentar: LangText.nullable().default(null),
    fotos: z.array(Id).default([]),
});
/**
 * Eine neue Buchung mitsamt den Fachregeln.
 *
 * Wird im Core-Dienst angewandt -- also auch dann, wenn ein Modul im selben
 * Prozess bucht und gar nicht ueber HTTP kommt.
 */
export const BuchungAnlegen = Buchung.omit({ empfangen_am: true, folge: true })
    .extend({
    ziel_lagerort_id: Id.nullable().default(null),
    einsatzstelle_id: Id.nullable().default(null),
    menge: Menge.nullable().default(null),
    menge_absolut: Menge.nullable().default(null),
    status: Status.nullable().default(null),
    vorgang_id: Id.nullable().default(null),
    abgeleitet_von: Id.nullable().default(null),
    modul: z.string().max(64).nullable().default(null),
    kommentar: LangText.nullable().default(null),
    fotos: z.array(Id).default([]),
})
    .superRefine((b, ctx) => {
    const fehlt = (feld, warum) => {
        ctx.addIssue({ code: "custom", path: [feld], message: warum });
    };
    // Eine Buchung auf einen realen Lagerort braucht diesen Lagerort. Ohne ihn
    // waere der Artikel "irgendwo im Lager" -- die Frage, die das System
    // beantworten soll, bliebe offen.
    if ((b.art === "einbuchen" || b.art === "umlagerung" || b.art === "ruecknahme") &&
        b.ziel_lagerort_id === null) {
        fehlt("ziel_lagerort_id", `Bei "${b.art}" muss der Ziel-Lagerort angegeben sein`);
    }
    // Statusaenderungen sagen, welcher Status. Den Ort aendern sie nie -- und
    // einen Ort mitzuschicken waere ein Hinweis, dass jemand das erwartet.
    // Ob es den Status gibt, prueft der Core-Dienst gegen die Stammdaten.
    if (b.art === "statusaenderung" && b.status === null) {
        fehlt("status", 'Bei "statusaenderung" muss der Zielstatus angegeben sein');
    }
    if (b.art === "statusaenderung" && b.ziel_lagerort_id !== null) {
        fehlt("ziel_lagerort_id", 'Eine "statusaenderung" aendert nie den Ort -- dafuer gibt es "umlagerung"');
    }
    // Umgekehrt: Wer einbucht oder umlagert, setzt keinen Status mit.
    if (b.status !== null && b.art !== "statusaenderung" && b.art !== "korrektur") {
        fehlt("status", `Bei "${b.art}" wird kein Status gesetzt -- dafuer gibt es "statusaenderung"`);
    }
    // Korrekturen sind Gegenbuchungen mit Begruendung (CLAUDE.md §3.2). Eine
    // Korrektur ohne Grund ist in einem Monat nicht mehr nachvollziehbar.
    if (b.art === "korrektur" && (b.kommentar === null || b.kommentar.length === 0)) {
        fehlt("kommentar", "Eine Korrektur braucht eine Begruendung");
    }
    if (b.art === "korrektur" && b.status === null && b.menge_absolut === null) {
        fehlt("status", "Eine Korrektur muss einen Status oder einen Bestand setzen");
    }
    // `menge_absolut` ist die Ausnahme fuer die Inventur und nirgends sonst
    // erlaubt -- sonst umgeht man damit die Bewegungslogik.
    if (b.art !== "korrektur" && b.menge_absolut !== null) {
        fehlt("menge_absolut", 'Ein absoluter Bestand ist nur bei "korrektur" erlaubt');
    }
    // Eine abgeleitete Buchung verweist auf ihre Ausloeserin -- und nicht auf
    // sich selbst.
    if (b.abgeleitet_von !== null && b.abgeleitet_von === b.id) {
        fehlt("abgeleitet_von", "Eine Buchung kann nicht von sich selbst abgeleitet sein");
    }
});
//# sourceMappingURL=buchung.js.map